import { get } from './axios.config';
import tenantsService from './tenants.service';

/**
 * Serviço de métricas e dados para Admin
 */
const adminService = {
  /**
   * Busca status de saúde do sistema
   * Endpoint: GET /health
   * @returns {Promise<Object>}
   */
  getHealth: async () => {
    try {
      const response = await get('/health');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao buscar status do sistema' };
    }
  },

  /**
   * Calcula métricas agregadas de todos os tenants
   * @returns {Promise<Object>}
   */
  calculateMetrics: async () => {
    try {
      // Busca todos os tenants
      const tenants = await tenantsService.getAll();
      
      // Inicializa contadores
      let totalConversations = 0;
      let openConversations = 0;
      let closedConversations = 0;
      let botPausedCount = 0;
      let conversationsByTenant = [];

      console.log('📊 Calculando métricas para', tenants.length, 'tenants');

      // Para cada tenant, busca conversas usando token temporário
      for (const tenant of tenants) {
        try {
          console.log(`🔍 Processando tenant: ${tenant.id}`);

          // ⬅️ GERAR TOKEN TEMPORÁRIO PARA CADA TENANT
          let authToken;
          try {
            const tokenData = await tenantsService.generateToken(tenant.id);
            authToken = tokenData.token;
          } catch (error) {
            console.error(`❌ Erro ao gerar token para ${tenant.id}:`, error);
            conversationsByTenant.push({
              tenantId: tenant.id,
              tenantName: tenant.name,
              open: 0,
              closed: 0,
              total: 0,
              botPaused: 0,
              error: true,
            });
            continue;
          }

          // ⬅️ BUSCAR CONVERSAS USANDO FETCH COM TOKEN CORRETO
          const [openResponse, closedResponse] = await Promise.all([
            fetch(`https://bot-api.maralabs.com.br/api/tenant/${tenant.id}/conversations?status=open&limit=200`, {
              headers: { 'Authorization': `Bearer ${authToken}` }
            }),
            fetch(`https://bot-api.maralabs.com.br/api/tenant/${tenant.id}/conversations?status=closed&limit=200`, {
              headers: { 'Authorization': `Bearer ${authToken}` }
            })
          ]);

          let openList = [];
          let closedList = [];

          if (openResponse.ok) {
            const openData = await openResponse.json();
            openList = openData.items || openData.conversations || [];
          }

          if (closedResponse.ok) {
            const closedData = await closedResponse.json();
            closedList = closedData.items || closedData.conversations || [];
          }

          const pausedCount = openList.filter(c => c.bot_paused).length;

          totalConversations += openList.length + closedList.length;
          openConversations += openList.length;
          closedConversations += closedList.length;
          botPausedCount += pausedCount;

          console.log(`✅ ${tenant.id}: ${openList.length} abertas, ${closedList.length} fechadas`);

          conversationsByTenant.push({
            tenantId: tenant.id,
            tenantName: tenant.name,
            open: openList.length,
            closed: closedList.length,
            total: openList.length + closedList.length,
            botPaused: pausedCount,
          });
        } catch (error) {
          console.error(`❌ Erro ao buscar conversas do tenant ${tenant.id}:`, error);
          conversationsByTenant.push({
            tenantId: tenant.id,
            tenantName: tenant.name,
            open: 0,
            closed: 0,
            total: 0,
            botPaused: 0,
            error: true,
          });
        }
      }

      const metrics = {
        totalTenants: tenants.length,
        activeTenants: tenants.filter(t => t.isActive).length,
        inactiveTenants: tenants.filter(t => !t.isActive).length,
        botsActive: tenants.filter(t => t.isActive && !t.botPaused).length,
        botsPaused: tenants.filter(t => t.botPaused).length,
        sendEnabled: tenants.filter(t => !t.sendDisabled).length,
        sendDisabled: tenants.filter(t => t.sendDisabled).length,
        coexEnabled: tenants.filter(t => t.coexEnable).length,
        totalConversations,
        openConversations,
        closedConversations,
        botPausedCount,
        resolutionRate: totalConversations > 0 
          ? ((closedConversations / totalConversations) * 100).toFixed(1)
          : 0,
        conversationsByTenant,
      };

      console.log('✅ Métricas finais:', metrics);
      return metrics;
    } catch (error) {
      console.error('❌ Erro ao calcular métricas:', error);
      throw error;
    }
  },

  /**
   * Busca métricas de um tenant específico
   * @param {string} tenantId 
   * @returns {Promise<Object>}
   */
  getTenantMetrics: async (tenantId) => {
    try {
      const tenant = await tenantsService.getById(tenantId);
      
      // Gerar token temporário
      const tokenData = await tenantsService.generateToken(tenantId);
      const authToken = tokenData.token;

      // Buscar conversas
      const [openResponse, closedResponse] = await Promise.all([
        fetch(`https://bot-api.maralabs.com.br/api/tenant/${tenantId}/conversations?status=open&limit=200`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }),
        fetch(`https://bot-api.maralabs.com.br/api/tenant/${tenantId}/conversations?status=closed&limit=200`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        })
      ]);

      let openConversationsList = [];
      let closedConversationsList = [];

      if (openResponse.ok) {
        const openData = await openResponse.json();
        openConversationsList = openData.items || openData.conversations || [];
      }

      if (closedResponse.ok) {
        const closedData = await closedResponse.json();
        closedConversationsList = closedData.items || closedData.conversations || [];
      }

      const totalConversations = openConversationsList.length + closedConversationsList.length;

      return {
        tenant,
        totalConversations,
        openConversations: openConversationsList.length,
        closedConversations: closedConversationsList.length,
        botPaused: openConversationsList.filter(c => c.bot_paused).length,
        resolutionRate: totalConversations > 0
          ? ((closedConversationsList.length / totalConversations) * 100).toFixed(1)
          : 0,
        recentConversations: openConversationsList.slice(0, 5),
      };
    } catch (error) {
      console.error('Erro ao buscar métricas do tenant:', error);
      throw error;
    }
  },
};

export default adminService;